<template>
  <div class="tab-content">
    <!-- Суммарные метрики -->
    <div class="stats-grid stats-grid--3">
      <div class="metric-card metric-card--invoices">
        <div class="metric-card__header">
          <span class="metric-card__title">Выручка</span>
          <span class="metric-card__icon">💼</span>
        </div>
        <div class="metric-card__value-row">
          <span class="metric-card__value">
            {{ formatMoney(totals.earnedAmount) }}
            <span class="metric-card__value-full">
              {{ formatMoneyFull(totals.earnedAmount) }}
            </span>
          </span>
        </div>
        <span class="metric-card__sub">по всем ТС</span>
      </div>

      <div class="metric-card metric-card--expenses">
        <div class="metric-card__header">
          <span class="metric-card__title">Расходы</span>
          <span class="metric-card__icon">💸</span>
        </div>
        <div class="metric-card__value-row">
          <span class="metric-card__value">
            {{ formatMoney(totals.expensesAmount) }}
            <span class="metric-card__value-full">
              {{ formatMoneyFull(totals.expensesAmount) }}
            </span>
          </span>
        </div>
        <span class="metric-card__sub">по всем ТС</span>
      </div>

      <div
        class="metric-card metric-card--profit"
        :class="
          totals.profitAmount >= 0
            ? 'metric-card--profit-pos'
            : 'metric-card--profit-neg'
        "
      >
        <div class="metric-card__header">
          <span class="metric-card__title">Прибыль</span>
          <span class="metric-card__icon">
            {{ totals.profitAmount >= 0 ? "📈" : "📉" }}
          </span>
        </div>
        <div class="metric-card__value-row">
          <span class="metric-card__value">
            {{ formatMoney(totals.profitAmount) }}
            <span class="metric-card__value-full">
              {{ formatMoneyFull(totals.profitAmount) }}
            </span>
          </span>
        </div>
        <span class="metric-card__sub">выручка минус расходы</span>
      </div>
    </div>

    <!-- График -->
    <div v-if="hasChartData" class="card chart-card">
      <div class="chart-card__header">
        <h4>Динамика по транспорту</h4>
        <div class="filter-tabs">
          <button
            class="filter-tab"
            :class="{ active: effectiveChartType === 'line' }"
            :disabled="effectiveChartType === 'line'"
            @click="chartType = 'line'"
          >
            Линейный
          </button>
          <button
            class="filter-tab"
            :class="{ active: effectiveChartType === 'donut' }"
            :disabled="effectiveChartType === 'donut'"
            @click="chartType = 'donut'"
          >
            Круговой
          </button>
          <button
            v-if="!barTooManyPoints"
            class="filter-tab"
            :class="{ active: effectiveChartType === 'bar' }"
            :disabled="effectiveChartType === 'bar'"
            @click="chartType = 'bar'"
          >
            Столбчатый
          </button>
        </div>
      </div>

      <LineChart
        v-if="effectiveChartType === 'line'"
        :series="lineChartSeries"
        :points="chartPoints"
        :height="240"
        :format-value="formatChartValue"
      />
      <DonutChart
        v-else-if="effectiveChartType === 'donut'"
        :slices="summaryDonutSlices"
        :format-value="formatMoney"
        center-title="Итого"
      />
      <BarChart
        v-else-if="effectiveChartType === 'bar'"
        :series="barChartSeries"
        :points="chartPoints"
        :height="240"
        :format-value="formatChartValue"
      />
    </div>

    <!-- Разбивка по ТС -->
    <div v-if="hasStatusData" class="card status-card">
      <div class="status-card__header">
        <h4>По транспортным средствам</h4>

        <div class="status-card__header-right">
          <div class="filter-tabs">
            <button
              v-for="m in donutMetrics"
              :key="m.key"
              class="filter-tab"
              :class="{ active: donutMetric === m.key }"
              :disabled="donutMetric === m.key"
              @click="donutMetric = m.key"
            >
              {{ m.label }}
            </button>
          </div>

          <div class="filter-tabs">
            <button
              class="filter-tab"
              :class="{ active: statusViewType === 'donut' }"
              :disabled="statusViewType === 'donut'"
              @click="statusViewType = 'donut'"
            >
              Диаграмма
            </button>
            <button
              class="filter-tab"
              :class="{ active: statusViewType === 'bars' }"
              :disabled="statusViewType === 'bars'"
              @click="statusViewType = 'bars'"
            >
              Список
            </button>
          </div>
        </div>
      </div>

      <div class="status-card__metric-summary">
        <span class="status-card__metric-label">
          {{ activeMetric.label }}:
        </span>
        <strong>{{ formatMoneyFull(activeMetricTotal) }}</strong>
      </div>

      <DonutChart
        v-if="statusViewType === 'donut'"
        :slices="vehicleSlices"
        :format-value="formatMoney"
        :center-title="activeMetric.label"
      />
      <PercentageLineChart
        v-else
        :slices="vehicleSlices"
        :format-value="formatMoney"
      />
    </div>
  </div>
</template>

<script>
import LineChart from "@/components/Dashboard/Charts/LineChart.vue";
import BarChart from "@/components/Dashboard/Charts/BarChart.vue";
import DonutChart from "@/components/Dashboard/Charts/DonutChart.vue";
import PercentageLineChart from "@/components/Dashboard/Charts/PercentageLineChart.vue";

import {
  formatMoney,
  formatChartValue,
  formatDisplayDate,
} from "@/utils/dashboardFormatters";

import { useVehicleStore } from "@/stores/vehicleStore";

const VEHICLE_COLORS = [
  "#4c6ef5",
  "#00bfa5",
  "#ca8a04",
  "#f97316",
  "#a855f7",
  "#ec4899",
  "#06b6d4",
  "#84cc16",
];

const DONUT_METRICS = [
  { key: "earned", label: "Выручка" },
  { key: "expenses", label: "Расходы" },
  { key: "profit", label: "Прибыль" },
];

const CHART_TYPE_KEY = "dashboard:chart:chartType";
const VALID_CHART_TYPES = ["line", "bar", "donut"];
const STATUS_VIEW_TYPE_KEY = "dashboard:chart:statusViewType";
const VALID_STATUS_VIEW_TYPES = ["donut", "bars"];
const DONUT_METRIC_KEY = "dashboard:vehicle:donutMetric";
const VALID_DONUT_METRICS = ["earned", "expenses", "profit"];
const MAX_BAR_POINTS = 60;

export default {
  name: "DashboardVehicleTab",

  components: {
    LineChart,
    BarChart,
    DonutChart,
    PercentageLineChart,
  },

  props: {
    periods: {
      type: Array,
      required: true,
    },
  },

  data() {
    const savedChartType = localStorage.getItem(CHART_TYPE_KEY);
    const initialChartType = VALID_CHART_TYPES.includes(
      savedChartType
    )
      ? savedChartType
      : "line";

    const savedStatusViewType = localStorage.getItem(
      STATUS_VIEW_TYPE_KEY
    );
    const initialStatusViewType = VALID_STATUS_VIEW_TYPES.includes(
      savedStatusViewType
    )
      ? savedStatusViewType
      : "donut";

    const savedDonutMetric = localStorage.getItem(DONUT_METRIC_KEY);
    const initialDonutMetric = VALID_DONUT_METRICS.includes(
      savedDonutMetric
    )
      ? savedDonutMetric
      : "earned";

    return {
      vehicleStore: useVehicleStore(),
      chartType: initialChartType,
      effectiveChartType: initialChartType,
      statusViewType: initialStatusViewType,
      donutMetric: initialDonutMetric,
      donutMetrics: DONUT_METRICS,
      lineChartSeries: [
        { key: "earned", label: "Выручка", color: "#00bfa5" },
        { key: "expenses", label: "Расходы", color: "#d64747" },
      ],
      barChartSeries: [
        { key: "earned", label: "Выручка", color: "#00bfa5" },
        { key: "expenses", label: "Расходы", color: "#d64747" },
        { key: "profit", label: "Прибыль", color: "#4c6ef5" },
      ],
    };
  },

  computed: {
    barTooManyPoints() {
      return this.periods.length > MAX_BAR_POINTS;
    },

    hasChartData() {
      return (
        this.periods.length > 1 &&
        this.chartPoints.some((p) =>
          Object.values(p.values).some((v) => v > 0)
        )
      );
    },

    hasStatusData() {
      return (
        this.totals.earnedAmount > 0 ||
        this.totals.expensesAmount > 0 ||
        this.totals.profitAmount !== 0
      );
    },

    totals() {
      return this.periods.reduce(
        (acc, period) => {
          for (const [vehicleId, data] of Object.entries(
            period.vehicles || {}
          )) {
            acc.earnedAmount += parseFloat(data.earned_amount) || 0;
            acc.expensesAmount +=
              parseFloat(data.expenses_amount) || 0;
            acc.profitAmount += parseFloat(data.profit_amount) || 0;

            if (!acc.byVehicle[vehicleId]) {
              acc.byVehicle[vehicleId] = {
                earnedAmount: 0,
                expensesAmount: 0,
                profitAmount: 0,
              };
            }
            acc.byVehicle[vehicleId].earnedAmount +=
              parseFloat(data.earned_amount) || 0;
            acc.byVehicle[vehicleId].expensesAmount +=
              parseFloat(data.expenses_amount) || 0;
            acc.byVehicle[vehicleId].profitAmount +=
              parseFloat(data.profit_amount) || 0;
          }
          return acc;
        },
        {
          earnedAmount: 0,
          expensesAmount: 0,
          profitAmount: 0,
          byVehicle: {},
        }
      );
    },

    chartPoints() {
      return this.periods.map((p) => {
        let earned = 0;
        let expenses = 0;
        let profit = 0;
        for (const data of Object.values(p.vehicles || {})) {
          earned += parseFloat(data.earned_amount) || 0;
          expenses += parseFloat(data.expenses_amount) || 0;
          profit += parseFloat(data.profit_amount) || 0;
        }
        return {
          label: formatDisplayDate(p.period_start, p.period_end),
          values: { earned, expenses, profit },
        };
      });
    },

    // Два слайса для кругового графика в chart-card
    summaryDonutSlices() {
      return [
        {
          key: "earned",
          label: "Выручка",
          color: "#00bfa5",
          value: this.totals.earnedAmount,
          count: null,
        },
        {
          key: "expenses",
          label: "Расходы",
          color: "#d64747",
          value: this.totals.expensesAmount,
          count: null,
        },
      ];
    },

    vehicleRows() {
      return Object.entries(this.totals.byVehicle).map(
        ([key, data], index) => {
          const stored = this.vehicleStore.vehicles.find(
            (v) => String(v.id) === String(key)
          );
          const label = stored ? stored.full_name : `ТС #${key}`;

          return {
            key,
            label,
            color: VEHICLE_COLORS[index % VEHICLE_COLORS.length],
            earnedAmount: data.earnedAmount,
            expensesAmount: data.expensesAmount,
            profitAmount: data.profitAmount,
          };
        }
      );
    },

    activeMetric() {
      return DONUT_METRICS.find((m) => m.key === this.donutMetric);
    },

    activeMetricTotal() {
      const map = {
        earned: this.totals.earnedAmount,
        expenses: this.totals.expensesAmount,
        profit: this.totals.profitAmount,
      };
      return map[this.donutMetric] ?? 0;
    },

    vehicleSlices() {
      return this.vehicleRows.map((v) => {
        const valueMap = {
          earned: v.earnedAmount,
          expenses: v.expensesAmount,
          profit: v.profitAmount,
        };
        const rawValue = valueMap[this.donutMetric] ?? 0;

        return {
          key: v.key,
          label: v.label,
          color: v.color,
          value: Math.max(0, rawValue),
          rawValue,
          count: null,
        };
      });
    },
  },

  watch: {
    chartType(value) {
      localStorage.setItem(CHART_TYPE_KEY, value);
      if (value === "bar" && this.barTooManyPoints) {
        this.effectiveChartType = "line";
      } else {
        this.effectiveChartType = value;
      }
    },

    barTooManyPoints(tooMany) {
      if (tooMany && this.chartType === "bar") {
        this.effectiveChartType = "line";
      } else {
        this.effectiveChartType = this.chartType;
      }
    },

    statusViewType(value) {
      localStorage.setItem(STATUS_VIEW_TYPE_KEY, value);
    },

    donutMetric(value) {
      localStorage.setItem(DONUT_METRIC_KEY, value);
    },

    "totals.byVehicle"(newVal) {
      this.vehicleStore.ensureVehiclesLoaded(Object.keys(newVal));
    },
  },

  methods: {
    formatMoney,
    formatChartValue,

    formatMoneyFull(v) {
      return (
        Number(v).toLocaleString("ru-RU", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }) + " ₽"
      );
    },
  },

  async created() {
    if (this.chartType === "bar" && this.barTooManyPoints) {
      this.effectiveChartType = "line";
    }

    if (!this.vehicleStore.vehicles.length) {
      await this.vehicleStore.fetchVehicles();
    }
    await this.vehicleStore.ensureVehiclesLoaded(
      Object.keys(this.totals.byVehicle)
    );
  },
};
</script>

<style scoped>
.tab-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: fadeIn 0.25s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ── Метрики ── */
.stats-grid {
  display: grid;
  gap: 16px;
}

.stats-grid--3 {
  grid-template-columns: repeat(3, 1fr);
}

.metric-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: var(--base-in-indent);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border-top: 3px solid transparent;
  position: relative;
  overflow: hidden;
}

.metric-card::before {
  content: "";
  position: absolute;
  inset: 0;
  opacity: 0.04;
  pointer-events: none;
}

.metric-card--invoices {
  border-top-color: #00bfa5;
}
.metric-card--invoices::before {
  background: #00bfa5;
}
.metric-card--expenses {
  border-top-color: #d64747;
}
.metric-card--expenses::before {
  background: #d64747;
}
.metric-card--profit-pos {
  border-top-color: #4c6ef5;
}
.metric-card--profit-pos::before {
  background: #4c6ef5;
}
.metric-card--profit-neg {
  border-top-color: #d64747;
}
.metric-card--profit-neg::before {
  background: #d64747;
}

.metric-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.metric-card__title {
  font-size: 13px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metric-card__icon {
  font-size: 22px;
  line-height: 1;
}

.metric-card__value-row {
  position: relative;
  display: inline-block;
}

.metric-card__value {
  position: relative;
  display: inline-block;
  font-size: 28px;
  font-weight: 700;
  color: var(--color-dark);
  line-height: 1;
  letter-spacing: -0.5px;
  cursor: default;
}

.metric-card--profit-neg .metric-card__value {
  color: var(--color-danger);
}
.metric-card--profit-pos .metric-card__value {
  color: var(--color-green);
}

.metric-card__value-full {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  background: #333;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0;
  white-space: nowrap;
  padding: 6px 10px;
  border-radius: 4px;
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease;
  z-index: 100;
}

.metric-card__value-full::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 16px;
  border: 4px solid transparent;
  border-top-color: #333;
}

.metric-card__value:hover .metric-card__value-full {
  opacity: 1;
  visibility: visible;
}

.metric-card__sub {
  font-size: 12px;
  color: #bbb;
  margin-top: auto;
}

/* ── График ── */
.chart-card {
  gap: 16px;
}

.chart-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

/* ── Общие табы ── */
.filter-tabs {
  display: flex;
  gap: 4px;
  background: var(--color-grey);
  padding: 3px;
  border-radius: 8px;
  flex-shrink: 0;
}

.filter-tab {
  padding: 7px 16px;
  border: none;
  background: transparent;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  color: #888;
  font-family: "Roboto", sans-serif;
  font-weight: 500;
}

.filter-tab.active {
  background: #fff;
  color: var(--color-dark);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
}

.filter-tab:disabled {
  cursor: default;
  pointer-events: none;
}

/* ── Разбивка по ТС ── */
.status-card {
  gap: 16px;
}

.status-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.status-card__header-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.status-card__metric-summary {
  margin-bottom: 15px;
  color: #999;
}

.status-card__metric-summary strong {
  font-size: 20px;
  font-weight: 700;
  color: var(--color-dark);
  margin-left: 4px;
}

/* ── Адаптив ── */
@media screen and (max-width: 1100px) {
  .stats-grid--3 {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media screen and (max-width: 767px) {
  .stats-grid--3 {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .metric-card__value {
    font-size: 22px;
  }

  .chart-card__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .status-card__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .status-card__header-right {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
  }
}

@media screen and (max-width: 480px) {
  .metric-card {
    padding: 14px;
    gap: 8px;
  }

  .metric-card__value {
    font-size: 19px;
  }

  .metric-card__icon {
    font-size: 18px;
  }
}
</style>
